import { useState, useEffect } from "react";
import { useClinic } from "@/contexts/ClinicContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, ShieldCheck, ChevronDown, ChevronUp, Search, Link, Link2Off, FileText } from "lucide-react";
import {
  fetchAuthorizationsByClinic,
  updateClaimStatus,
  InsuranceAuthorizationRequestDetail,
  InsuranceClaimResponse,
  ClaimStatus,
  CLAIM_STATUS_LABELS,
} from "@/services/insurance-authorization-service";
import {
  searchTissQueueByClaimNumber,
  linkTissQueueToRequest,
  unlinkTissQueueFromRequest,
  fetchLinkedGuides,
  TissExtractedData,
  TissQueueItem,
} from "@/services/tiss-queue-service";
import { ClaimDocumentsBlock } from "@/components/claim-documents/ClaimDocumentsBlock";

// Claims em ASSINADO ou posterior aceitam uploads (espelha DOCUMENT_ELIGIBLE_STATUSES do backend)
const DOC_ELIGIBLE_STATUSES: ClaimStatus[] = [
  "ASSINADO", "EM_ANALISE", "GLOSADO", "GLOSADO_PERDA", "GLOSA_RECURSO",
  "EM_RECEBIMENTO", "PAGO_CONVENIO", "NAO_PAGO_CONVENIO", "PARCIAL",
];

// Tela da recepcionista: só Fase 1 (SOLICITADO ↔ ASSINADO/NEGADO/CANCELADO)
// Fase 2 (EM_ANALISE em diante) será em tela separada do time de autorização
const NEXT_STATUSES: Partial<Record<ClaimStatus, ClaimStatus[]>> = {
  SOLICITADO: ["ASSINADO", "NEGADO", "CANCELADO"],
  ASSINADO: ["SOLICITADO", "NEGADO", "CANCELADO"],
  NEGADO: ["SOLICITADO", "ASSINADO"],
  CANCELADO: ["SOLICITADO"],
};

const STATUS_COLORS: Record<ClaimStatus, string> = {
  SOLICITADO: "bg-yellow-100 text-yellow-800 border-yellow-200",
  AUTORIZADO: "bg-green-100 text-green-800 border-green-200",
  NEGADO: "bg-red-100 text-red-800 border-red-200",
  CANCELADO: "bg-gray-100 text-gray-800 border-gray-200",
  ASSINADO: "bg-blue-100 text-blue-800 border-blue-200",
  EM_ANALISE: "bg-purple-100 text-purple-800 border-purple-200",
  GLOSADO: "bg-orange-100 text-orange-800 border-orange-200",
  GLOSADO_PERDA: "bg-red-100 text-red-800 border-red-200",
  GLOSA_RECURSO: "bg-orange-100 text-orange-800 border-orange-200",
  EM_RECEBIMENTO: "bg-cyan-100 text-cyan-800 border-cyan-200",
  PAGO_CONVENIO: "bg-green-100 text-green-800 border-green-200",
  NAO_PAGO_CONVENIO: "bg-red-100 text-red-800 border-red-200",
  PARCIAL: "bg-yellow-100 text-yellow-800 border-yellow-200",
};

function ClaimStatusBadge({ status }: { status: ClaimStatus }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${STATUS_COLORS[status]}`}>
      {CLAIM_STATUS_LABELS[status]}
    </span>
  );
}

function GuidePreview({ data, collapsible = false }: { data: TissExtractedData; collapsible?: boolean }) {
  const [open, setOpen] = useState(!collapsible);

  return (
    <div className="bg-muted/40 rounded text-xs">
      <div
        className={`flex items-center justify-between p-2 ${collapsible ? "cursor-pointer hover:bg-muted/60" : ""}`}
        onClick={() => collapsible && setOpen(v => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {collapsible && (open ? <ChevronUp className="h-3 w-3 text-muted-foreground shrink-0" /> : <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />)}
          <span className="text-muted-foreground font-mono">{data.claim_number}</span>
          <span className="font-medium truncate">{data.patient_name}</span>
        </div>
        <span className="text-muted-foreground shrink-0">R$ {data.valor_total}</span>
      </div>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          {data.procedures.length > 0 && (
            <ul className="space-y-0.5">
              {data.procedures.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-[11px]">
                  <span className="text-muted-foreground font-mono">{p.tiss_code}</span>
                  <span className="truncate">{p.procedure_name}</span>
                  {p.tooth_region && <span className="text-muted-foreground">({p.tooth_region}{p.tooth_face ? `/${p.tooth_face}` : ""})</span>}
                  <span className="text-muted-foreground ml-auto">R$ {p.value}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function GuieLinkSection({ requestId, canUnlink, onLinked }: { requestId: string; canUnlink: boolean; onLinked: () => void }) {
  const [claimNumber, setClaimNumber] = useState("");
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<{ id: string; label: string } | null>(null);
  const [found, setFound] = useState<{ queueId: string; data: TissExtractedData } | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [linkedGuides, setLinkedGuides] = useState<{ id: string; data: TissExtractedData }[]>([]);
  const { toast } = useToast();

  const loadLinked = async () => {
    try {
      const items = await fetchLinkedGuides(requestId);
      setLinkedGuides(
        items
          .filter((item: TissQueueItem) => item.result_json)
          .map((item: TissQueueItem) => ({
            id: item.id,
            data: JSON.parse(item.result_json!) as TissExtractedData,
          }))
      );
    } catch {
      /* silencioso */
    }
  };

  useEffect(() => { loadLinked(); }, [requestId]);

  const handleSearch = async () => {
    if (!claimNumber.trim()) return;
    setSearching(true);
    setFound(null);
    setNotFound(false);
    try {
      const result = await searchTissQueueByClaimNumber(claimNumber.trim());
      if (result && result.result_json) {
        const data: TissExtractedData = JSON.parse(result.result_json);
        if (data.linked_request_id) {
          toast({ title: "Esta guia já está vinculada a outra solicitação.", variant: "destructive" });
          return;
        }
        setFound({ queueId: result.id, data });
      } else {
        setNotFound(true);
      }
    } catch (error) {
      toast({
        title: "Erro ao buscar guia",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    if (!found) return;
    setLinking(true);
    try {
      await linkTissQueueToRequest(found.queueId, requestId);
      toast({ title: "Guia vinculada com sucesso!" });
      setFound(null);
      setClaimNumber("");
      await loadLinked();
      onLinked();
    } catch (error) {
      toast({
        title: "Erro ao vincular guia",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLinking(false);
    }
  };

  const confirmUnlink = async () => {
    if (!pendingUnlink) return;
    const queueId = pendingUnlink.id;
    setPendingUnlink(null);
    setUnlinkingId(queueId);
    try {
      await unlinkTissQueueFromRequest(queueId);
      toast({ title: "Guia desvinculada." });
      await loadLinked();
      onLinked();
    } catch (error) {
      toast({
        title: "Erro ao desvincular",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setUnlinkingId(null);
    }
  };

  return (
    <div className="space-y-2 pt-2 border-t border-border">
      {/* Guias vinculadas */}
      {linkedGuides.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold flex items-center gap-1">
            <FileText className="h-3 w-3" /> Guias Vinculadas ({linkedGuides.length})
          </p>
          {linkedGuides.map((g) => (
            <div key={g.id} className="space-y-1">
              <GuidePreview data={g.data} collapsible />
              {canUnlink && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] text-muted-foreground hover:text-destructive"
                    onClick={() => setPendingUnlink({ id: g.id, label: `${g.data.claim_number} — ${g.data.patient_name}` })}
                    disabled={unlinkingId === g.id}
                  >
                    <Link2Off className="h-3 w-3 mr-1" />
                    {unlinkingId === g.id ? "Desvinculando..." : "Desvincular"}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingUnlink} onOpenChange={(open) => !open && setPendingUnlink(null)}>
        <AlertDialogContent className="max-w-[420px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Desvincular guia</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desvincular a guia <strong>{pendingUnlink?.label}</strong> desta solicitação?
              <br /><br />
              <span className="text-xs text-muted-foreground">O PDF volta para a fila como disponível para nova vinculação.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnlink}>Desvincular</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Vincular nova guia */}
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold flex items-center gap-1">
        <Link className="h-3 w-3" /> Vincular Guia
      </p>
      <div className="flex items-center gap-2">
        <Input
          placeholder="Nº da guia..."
          value={claimNumber}
          onChange={(e) => setClaimNumber(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="h-7 text-xs w-[180px]"
        />
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={handleSearch}
          disabled={searching || !claimNumber.trim()}
        >
          <Search className={`h-3 w-3 mr-1 ${searching ? "animate-spin" : ""}`} />
          Buscar
        </Button>
      </div>

      {notFound && (
        <p className="text-xs text-muted-foreground">Guia não encontrada na fila de PDFs extraídos.</p>
      )}

      {found && (
        <div className="space-y-1.5">
          <GuidePreview data={found.data} />
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={handleLink}
            disabled={linking}
          >
            <Link className="h-3 w-3 mr-1" />
            Vincular Guia
          </Button>
        </div>
      )}
    </div>
  );
}

function AuthorizationRow({ item, onStatusUpdated }: {
  item: InsuranceAuthorizationRequestDetail;
  onStatusUpdated: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [updatingClaimId, setUpdatingClaimId] = useState<string | null>(null);
  const { toast } = useToast();

  const latestClaim: InsuranceClaimResponse | undefined = item.claims?.[item.claims.length - 1];
  const currentStatus = latestClaim?.claim_status;

  const handleStatusChange = async (claimId: string, newStatus: ClaimStatus) => {
    setUpdatingClaimId(claimId);
    try {
      await updateClaimStatus(claimId, newStatus);
      toast({ title: "Status atualizado!" });
      onStatusUpdated();
    } catch (error) {
      toast({
        title: "Erro ao atualizar status",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setUpdatingClaimId(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="border border-border rounded-lg bg-card text-xs">
      {/* Row header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">
              {item.patient_name || "Paciente"}
              {(item.linked_guides_count ?? 0) > 0 && (
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 ml-1.5 rounded-full bg-primary/10 text-primary text-[9px] font-bold align-middle">
                  {item.linked_guides_count}
                </span>
              )}
            </span>
            <span className="text-muted-foreground text-[10px]">
              {item.appointment_date
                ? `${item.appointment_date.split("-").reverse().join("/")} ${item.appointment_time?.slice(0, 5) ?? ""}`
                : formatDate(item.created_at)}
              {item.dentist_name && ` · ${item.dentist_name}`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-muted-foreground hidden sm:inline truncate max-w-[180px]">
            {item.dental_insurance_name || "—"}
          </span>
          {currentStatus && <ClaimStatusBadge status={currentStatus} />}
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border p-3 space-y-3 bg-muted/20">
          {/* Description */}
          {item.description && (
            <div className="space-y-0.5">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Descrição</p>
              <p className="text-xs">{item.description}</p>
            </div>
          )}

          {/* Vincular Guia — desvincular só se ainda em SOLICITADO */}
          <GuieLinkSection
            requestId={item.id}
            canUnlink={currentStatus === "SOLICITADO"}
            onLinked={onStatusUpdated}
          />

          {/* Claims */}
          {item.claims?.map((claim) => {
            const nextOptions = NEXT_STATUSES[claim.claim_status] ?? [];
            const canUpload = DOC_ELIGIBLE_STATUSES.includes(claim.claim_status);
            return (
              <div key={claim.id} className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <ClaimStatusBadge status={claim.claim_status} />
                  {claim.claim_number && (
                    <span className="text-muted-foreground">Nº {claim.claim_number}</span>
                  )}
                  <span className="text-muted-foreground">{formatDate(claim.created_at)}</span>
                </div>

                {nextOptions.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Select
                      onValueChange={(v) => handleStatusChange(claim.id, v as ClaimStatus)}
                      disabled={updatingClaimId === claim.id}
                    >
                      <SelectTrigger className="h-7 text-xs w-[180px]">
                        <SelectValue placeholder="Atualizar status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {nextOptions.map(s => (
                          <SelectItem key={s} value={s}>{CLAIM_STATUS_LABELS[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {canUpload && (
                  <ClaimDocumentsBlock
                    claimId={claim.id}
                    onChanged={onStatusUpdated}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FILTER_STATUSES: ClaimStatus[] = ["SOLICITADO", "NEGADO", "ASSINADO", "CANCELADO"];

export default function InsuranceAuthorizationsPage() {
  const { selectedClinicId } = useClinic();
  const { toast } = useToast();

  const [items, setItems] = useState<InsuranceAuthorizationRequestDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | "ALL">("ALL");
  const [nameFilter, setNameFilter] = useState("");
  const [nameInput, setNameInput] = useState("");
  const todayISO = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayISO);
  const [dateTo, setDateTo] = useState(todayISO);

  const PAGE_SIZE = 20;

  const load = async () => {
    if (!selectedClinicId) return;
    setLoading(true);
    try {
      const result = await fetchAuthorizationsByClinic(selectedClinicId, page, PAGE_SIZE, {
        patientName: nameFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        claimStatus: statusFilter !== "ALL" ? statusFilter : undefined,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      toast({
        title: "Erro ao carregar autorizações",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedClinicId, page, statusFilter, nameFilter, dateFrom, dateTo]);

  useEffect(() => { setPage(1); }, [statusFilter, nameFilter, dateFrom, dateTo]);

  const applyNameFilter = () => setNameFilter(nameInput.trim());
  const clearFilters = () => {
    setNameInput("");
    setNameFilter("");
    setDateFrom(todayISO);
    setDateTo(todayISO);
    setStatusFilter("ALL");
  };

  return (
    <div className="page-wrapper space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="page-icon" />
            <h1 className="page-title">Autorizações de Convênio</h1>
            {total > 0 && (
              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground">
                {total}
              </span>
            )}
          </div>
          <p className="page-subtitle">
            Solicite autorizações, vincule guias TISS e anexe documentos assinados pelo paciente
          </p>
        </div>

        {/* Filtros */}
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Paciente</label>
            <Input
              placeholder="Parte do nome..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={applyNameFilter}
              onKeyDown={(e) => e.key === "Enter" && applyNameFilter()}
              className="h-7 text-xs w-[200px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">De</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-7 text-xs w-[140px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Até</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-7 text-xs w-[140px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Status</label>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as ClaimStatus | "ALL")}
            >
              <SelectTrigger className="h-7 text-xs w-[160px]">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                {FILTER_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{CLAIM_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" className="btn-header gap-1.5" onClick={() => load()} disabled={loading}>
            <Search className={`btn-header-icon ${loading ? "animate-spin" : ""}`} /> Filtrar
          </Button>
          <Button variant="outline" size="sm" className="btn-header" onClick={clearFilters}>
            Limpar
          </Button>
        </div>
      </div>

      {/* No clinic selected */}
      {!selectedClinicId && (
        <p className="text-xs text-muted-foreground text-center py-8">Selecione uma clínica no topo da tela.</p>
      )}

      {/* List */}
      {selectedClinicId && (
        <>
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">Nenhuma autorização encontrada.</p>
          ) : (
            <div className="space-y-2">
              {items.map(item => (
                <AuthorizationRow key={item.id} item={item} onStatusUpdated={load} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}>
                Anterior
              </Button>
              <span className="text-xs text-muted-foreground self-center">
                Página {page} de {Math.ceil(total / PAGE_SIZE)}
              </span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / PAGE_SIZE) || loading}>
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
